import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { VehicleTypeService } from 'src/app/services/vehicle-type/vehicle-type.service';
import { VehicleTypeModalService } from 'src/app/services/vehicle-type-modal/vehicle-type-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-vehicle-type',
  templateUrl: './add-edit-vehicle-type.component.html',
  styleUrls: ['./add-edit-vehicle-type.component.scss']
})
export class AddEditVehicleTypeComponent extends DestructorComponent implements OnInit {

  @Output() vehicleTypeAdded = new EventEmitter<void>();
  @Output() vehicleTypeUpdated = new EventEmitter<void>();

  vehicleTypeForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedVehicleType: any = null;

  constructor(
    private fb: FormBuilder,
    private _vehicleTypeService: VehicleTypeService,
    private _vehicleTypeModalService: VehicleTypeModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.vehicleTypeForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      m3: ['', [Validators.required]]
    });
  }

  // Getter methods for form controls
  get name() { return this.vehicleTypeForm.get('name'); }
  get m3() { return this.vehicleTypeForm.get('m3'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_vehicle_type_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceVehicleType = this._vehicleTypeModalService.getSelectedVehicleType();
        if (serviceVehicleType) {
          this.isEditMode = true;
          this.selectedVehicleType = serviceVehicleType;
          this.populateForm(this.selectedVehicleType);
        } else {
          this.isEditMode = false;
          this.selectedVehicleType = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._vehicleTypeModalService.clearSelectedVehicleType();
        this.resetForm();
      });
    }
  }

  private populateForm(vehicleType: any): void {
    this.vehicleTypeForm.patchValue({
      name: vehicleType.name || '',
      m3: vehicleType.m3 || ''
    });
  }

  private resetForm(): void {
    this.vehicleTypeForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.vehicleTypeForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_vehicle_type_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.vehicleTypeForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.vehicleTypeForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._vehicleTypeService.UpdateData(this.selectedVehicleType._id, submitData)
        : this._vehicleTypeService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Vehicle type operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Vehicle type has been successfully updated!"
                : "Vehicle type has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.vehicleTypeUpdated.emit();
                } else {
                  this.vehicleTypeAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Vehicle type operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.vehicleTypeForm);
    }
  }
}
